<template>
  <CModal
    title="Add employee"
    size="lg"
    :show="show"
    @update:show="$emit('close')"
  >
    <CRow>
      <CCol md="6">
        <CInput
          ref="fullNameInput"
          label="Full name *"
          class="mb-3"
          v-model="localEmployee.fullName"
          placeholder="As shown on their IC"
        />

        <CInput
          label="Email *"
          class="mb-3"
          v-model="localEmployee.email"
          placeholder="name@company.com"
          type="email"
          description="They sign in with this email"
          :is-valid="emailError ? false : null"
          :invalid-feedback="emailError"
        />

        <CInput
          label="Date of birth *"
          class="mb-3"
          v-model="localEmployee.dateOfBirth"
          type="date"
          description="Used to work out EPF, SOCSO and EIS rates by age"
        />
      </CCol>

      <CCol md="6">
        <CInput
          label="Basic salary"
          class="mb-3"
          v-model="localEmployee.basicSalary"
          type="number"
          step="0.01"
          min="0"
          inputmode="decimal"
          prepend="RM"
          placeholder="0.00"
          description="Monthly, before deductions"
        />

        <div class="mb-3">
          <label class="mb-2" for="employee-modal-role">Role *</label>
          <v-select
            input-id="employee-modal-role"
            v-model="localEmployee.appUser.role"
            :options="roleOptions"
            placeholder="Choose a role"
            :clearable="false"
          />
        </div>

        <div class="mb-3">
          <label class="mb-2" for="employee-modal-status">Employment status</label>
          <v-select
            input-id="employee-modal-status"
            v-model="localEmployee.employmentStatus"
            :options="statusOptions"
            :reduce="(o) => o.value"
            label="label"
            placeholder="Choose a status"
            :clearable="false"
          />
        </div>
      </CCol>
    </CRow>

    <!-- Password Section -->
    <CRow>
      <CCol xs="12">
        <CAlert color="info" class="mb-3">
          Set a first password for them. They can change it after they sign in.
        </CAlert>
      </CCol>
      <CCol xs="12">
        <CInput
          label="Password *"
          class="mb-3"
          v-model="plainPassword"
          type="password"
          autocomplete="new-password"
          description="At least 8 characters, with an uppercase letter, a lowercase letter, a number and a symbol"
          :is-valid="passwordError ? false : null"
          :invalid-feedback="passwordError"
        />
      </CCol>
    </CRow>

    <!-- Advanced Fields (Collapsible) -->
    <CRow>
      <CCol xs="12">
        <CButton
          color="link"
          class="p-0 mb-3"
          @click="showAdvanced = !showAdvanced"
        >
          <CIcon :name="showAdvanced ? 'cil-chevron-top' : 'cil-chevron-bottom'" class="mr-1" />
          {{ showAdvanced ? "Hide" : "Add" }} statutory and bank details
        </CButton>
      </CCol>
    </CRow>

    <CCollapse :show="showAdvanced">
      <CRow>
        <CCol md="6">
          <CInput
            id="employee-modal-epf"
            class="mb-3"
            v-model="localEmployee.epfNumber"
            inputmode="numeric"
          >
            <template #label>
              <label for="employee-modal-epf">EPF number <HrGlossaryTip term="EPF" /></label>
            </template>
          </CInput>

          <CInput
            id="employee-modal-socso"
            class="mb-3"
            v-model="localEmployee.socsoNumber"
          >
            <template #label>
              <label for="employee-modal-socso">SOCSO number <HrGlossaryTip term="SOCSO" /></label>
            </template>
          </CInput>

          <CInput
            label="Tax number"
            class="mb-3"
            v-model="localEmployee.taxNumber"
            description="Income tax number from LHDN"
          />

          <CInput
            label="Join date"
            class="mb-3"
            v-model="localEmployee.joinDate"
            type="date"
          />
        </CCol>

        <CCol md="6">
          <CInput
            label="Bank"
            class="mb-3"
            v-model="localEmployee.bankName"
            placeholder="For example, Maybank"
          />

          <CInput
            label="Account name"
            class="mb-3"
            v-model="localEmployee.bankAccountName"
          />

          <CInput
            label="Account number"
            class="mb-3"
            inputmode="numeric"
            v-model="localEmployee.bankAccountNumber"
          />

          <div class="form-check mb-3">
            <input
              class="form-check-input"
              type="checkbox"
              v-model="localEmployee.isEPFOptedOut"
              id="epfOptOut"
            />
            <label class="form-check-label" for="epfOptOut">
              Opted out of EPF (aged 55 to 60 only)
            </label>
          </div>
        </CCol>
      </CRow>
    </CCollapse>

    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <CButton
          color="link"
          @click="openFullForm"
          class="px-0"
        >
          Open the full form
        </CButton>
        <div>
          <CButton color="light" @click="$emit('close')" class="mr-2">
            Cancel
          </CButton>
          <CButton color="primary" @click="saveEmployee" :disabled="!isValid">
            Add employee
          </CButton>
        </div>
      </div>
    </template>
  </CModal>
</template>

<script>
import HrGlossaryTip from "./HrGlossaryTip";

export default {
  name: "EmployeeFormModal",
  components: { HrGlossaryTip },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      localEmployee: {
        fullName: '',
        email: '',
        dateOfBirth: null,
        basicSalary: null,
        epfNumber: '',
        socsoNumber: '',
        taxNumber: '',
        bankName: '',
        bankAccountName: '',
        bankAccountNumber: '',
        joinDate: null,
        employmentStatus: 'Active',
        isEnable: true,
        isEPFOptedOut: false,
        appUser: {
          role: 'Staff'
        }
      },
      plainPassword: '',
      showAdvanced: false,
      roleOptions: ['Staff', 'Accountant'],
      statusOptions: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
        { value: 'OnLeave', label: 'On leave' },
      ],
      emailError: '',
      passwordError: '',
    };
  },
  watch: {
    employee: {
      handler(newEmployee) {
        this.localEmployee = {
          fullName: '',
          email: '',
          dateOfBirth: null,
          basicSalary: null,
          epfNumber: '',
          socsoNumber: '',
          taxNumber: '',
          bankName: '',
          bankAccountName: '',
          bankAccountNumber: '',
          joinDate: null,
          employmentStatus: 'Active',
          isEnable: true,
          isEPFOptedOut: false,
          appUser: {
            role: 'Staff'
          },
          ...newEmployee
        };
      },
      deep: true,
    },
    show(newValue) {
      if (newValue) {
        this.localEmployee = {
          fullName: '',
          email: '',
          dateOfBirth: null,
          basicSalary: null,
          epfNumber: '',
          socsoNumber: '',
          taxNumber: '',
          bankName: '',
          bankAccountName: '',
          bankAccountNumber: '',
          joinDate: null,
          employmentStatus: 'Active',
          isEnable: true,
          isEPFOptedOut: false,
          appUser: {
            role: 'Staff'
          },
          ...this.employee
        };
        this.plainPassword = '';
        this.emailError = '';
        this.passwordError = '';
        this.showAdvanced = false;

        // Auto-focus on full name field
        this.$nextTick(() => {
          if (this.$refs.fullNameInput && this.$refs.fullNameInput.$el) {
            this.$refs.fullNameInput.$el.querySelector('input').focus();
          }
        });
      }
    },
    'localEmployee.email'(newEmail) {
      this.validateEmail(newEmail);
    },
    plainPassword(newPassword) {
      this.validatePassword(newPassword);
    }
  },
  computed: {
    isValid() {
      return (
        this.localEmployee.fullName &&
        this.localEmployee.fullName.trim() !== '' &&
        this.localEmployee.email &&
        this.localEmployee.email.trim() !== '' &&
        this.localEmployee.dateOfBirth &&
        this.localEmployee.appUser.role &&
        this.plainPassword &&
        this.plainPassword.trim() !== '' &&
        !this.emailError &&
        !this.passwordError
      );
    },
  },
  methods: {
    validateEmail(email) {
      if (!email || email.trim() === '') {
        this.emailError = '';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.emailError = 'Enter an email address like name@company.com.';
      } else {
        this.emailError = '';
      }
    },

    validatePassword(password) {
      if (!password || password.trim() === '') {
        this.passwordError = 'Enter a password.';
        return;
      }

      if (password.length < 8) {
        this.passwordError = 'Use at least 8 characters.';
        return;
      }

      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
        this.passwordError = 'Add an uppercase letter, a lowercase letter, a number and a symbol such as !';
        return;
      }

      this.passwordError = '';
    },

    saveEmployee() {
      if (this.isValid) {
        // Ensure appUser structure exists
        const employeeData = {
          profile: {
            ...this.localEmployee,
            appUser: {
              role: this.localEmployee.appUser.role || 'Staff'
            }
          },
          isResetPassword: true,
          plainPassword: this.plainPassword,
          role: this.localEmployee.appUser.role || 'Staff'
        };
        this.$emit('save', employeeData);
      }
    },

    openFullForm() {
      // Open full employee form in new tab
      const route = this.$router.resolve({ name: 'Employee' });
      window.open(route.href, '_blank');
    }
  },
};
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  margin-bottom: 0.5rem;
  display: block;
}

.form-check {
  padding-left: 1.5rem;
}

.form-check-input {
  margin-top: 0.3rem;
}
</style>
